// Forsyt Plugin Store catalogue.
// Each plugin is deeply specified. We do not inflate the store with thin
// offerings. Every entry here maps to a real capability requested by a
// revenue leader running on Forsyt.

export const PLUGINS = [
  {
    slug: 'establishment-grader',
    name: 'Establishment Grader',
    version: '0.9.1',
    tagline:
      'Grade a lead by how established its business actually is, before you spend a call on it.',
    category: 'Sales · Lead Qualification',
    pricing: 'Private beta',
    status: 'Private beta',
    accent: '#4f46e5',
    icon: 'gauge',
    verified: true,
    author: 'Forsyt Labs',
    author_role: 'Built in-house · maintained by Forsyt',
    requested_by: {
      designation: 'VP of Inside Sales',
      company: 'a Series B B2B lending platform',
      region: 'India & Southeast Asia',
    },
    requested_context:
      'Their SDR team was dialling 120 businesses a day off a bought list, and roughly two in five turned out to be dormant shells, single-person side hustles, or firms that had shut down two years ago. The VP did not want another enrichment tool. He wanted one number that told a rep, before they picked up the phone, whether the business on the other end was real, operating, and the right size to buy. That number became Establishment Grader.',
    short:
      'Establishment Grader scores the establishment of a business, not just the person. Point it at a lead list and it returns an A to F grade per company built from registration status, operating age, digital and hiring footprint, funding and review signals, and legitimacy checks. Reps see the grade and the three signals that drove it right inside the deal view, so the weak rows never make it into a dialer queue.',
    why:
      'Most qualification scores lean on the contact: their title, their seniority, whether the email bounced. None of that matters if the company itself is a two-month-old shell with no address, no filings, and no staff. Reps learn this the hard way, one wasted call at a time. Establishment Grader flips the order. It asks the establishment question first, is this a real, operating, appropriately sized business, and only then lets the person-level signals refine the grade. The result is a queue where an A grade genuinely means "worth a human call today."',
    use_case: [
      {
        step: '01',
        title: 'Establishment check',
        body: 'For every company on the list, Establishment Grader verifies the corporate identity, how long it has been registered and operating, whether its registration is active, and whether it has a real, resolvable web and address footprint. Dormant, dissolved, or never-really-started businesses are flagged immediately.',
      },
      {
        step: '02',
        title: 'Operating signals',
        body: 'On top of legitimacy, it reads live signals of a running business: current hiring, headcount trajectory, funding or revenue bands, domain age and traffic, and volume and recency of customer reviews. These separate a genuinely active firm from one that merely exists on paper.',
      },
      {
        step: '03',
        title: 'Grade and rationale',
        body: 'Each company gets an A to F grade with a plain-language rationale and the top three signals that moved it. A rep never sees a black box, they see "Grade B, 9-year-old registered firm, active hiring, thin review footprint."',
      },
      {
        step: '04',
        title: 'Feeds the call queue',
        body: 'Grades sync back into Forsyt and your CRM. Reps can auto-suppress everything below a threshold, so a day\'s dialer queue is only businesses worth a human minute. No more calling ghosts.',
      },
    ],
    highlights: [
      'Business-establishment grade (A to F) computed per company, not per contact.',
      'Legitimacy layer: registration status, operating age, dissolved/dormant detection, address and domain resolution.',
      'Operating-signal layer: hiring activity, headcount band, funding/revenue estimate, review volume and recency.',
      'Plain-language rationale with the top three signals behind every grade, no black box.',
      'Configurable floor: auto-suppress leads below the grade you decide is worth a call.',
      'Grades write back to Forsyt deal views and to HubSpot, Salesforce, and Zoho.',
    ],
    inputs: [
      'CSV upload (company name or domain at minimum)',
      'CRM lead or account list (HubSpot, Salesforce, Zoho)',
      'Webhook payload from inbound forms',
    ],
    outputs: [
      'Business-establishment grade A to F per company',
      'Legitimacy status (active / dormant / dissolved / unverifiable)',
      'Top-three signal rationale per grade',
      'Suppression flag against your configured grade floor',
      'Write-back to CRM or download as CSV',
    ],
    works_with: ['HubSpot', 'Salesforce', 'Zoho', 'Pipedrive', 'Webhooks', 'CSV'],
    scopes: ['lead_record_write', 'firmographic_lookup', 'registry_lookup', 'icp_definition_read'],
    sources: [
      'Company & business registries (registration status, incorporation age)',
      'WHOIS + DNS (domain legitimacy and age)',
      'Hiring boards & careers pages (active-hiring signal)',
      'Crunchbase (funding, headcount band)',
      'Public review platforms (volume and recency)',
    ],
    performance: [
      { label: 'Throughput', value: '3,000 companies / minute (batch)' },
      { label: 'Median grade time', value: '2.1s per company (real-time)' },
      { label: 'Dormant/dissolved recall', value: '96.1% on a 20k audit set' },
      { label: 'Reps\u2019 call-connect lift', value: '+31% after suppression (pilot)' },
    ],
    screenshots: [
      {
        src: '/assets/plugins/eg-verified.webp',
        title: 'A verified business, unverified contact',
        caption:
          'TABP Snacks & Beverages grades YELLOW: a real, active private limited company (CIN found, incorporated 2018, Coimbatore) but the input phone does not reverse-map to a named person. Verdict: call the business through official channels, not the input number. Every claim is backed by a numbered evidence trail.',
      },
      {
        src: '/assets/plugins/eg-profile.webp',
        title: 'Full establishment + decision-maker read',
        caption:
          'SunMark International: verified CIN and registered address, directors surfaced as alternate contacts, plus a personality and pitch-angle profile for the buying committee. The input person could not be verified, so outreach is routed to the confirmed decision-makers.',
      },
      {
        src: '/assets/plugins/eg-discard.webp',
        title: 'A RED lead, safe to discard',
        caption:
          'Curious Minds Foundation School scores 1.6/10 and grades RED: no GSTIN or CIN, no official business listing, and a phone number that maps to unrelated people. Establishment Grader says discard, and shows exactly why.',
      },
    ],
    preview: null,
  },

  {
    slug: 'pipeline-health-cold-deal-tagging',
    name: 'Pipeline Health & Cold Deal Tagging',
    version: '1.0.0',
    tagline:
      'Find the deals that look alive on the board but have gone silent, before they wreck your forecast.',
    category: 'RevOps · Pipeline Hygiene',
    pricing: 'Live',
    status: 'Live',
    accent: '#c2410c',
    icon: 'snowflake',
    verified: true,
    author: 'Forsyt Labs',
    author_role: 'Built in-house · maintained by Forsyt',
    requested_by: {
      designation: 'Head of RevOps',
      company: 'a B2B SaaS company',
      region: 'India',
    },
    requested_context:
      'His pipeline looked healthy right up until the forecast missed, and then he was the one in the room explaining why. Deals sat in Proposal for weeks with zero prospect reply, but because a rep kept logging notes and nudging the stage, they still counted as active revenue. He wanted a system that quietly watched every deal, ignored rep-side busywork, and raised its hand the moment a prospect had actually gone quiet, so RevOps could get ahead of risk instead of getting blamed for it.',
    short:
      'Pipeline Health & Cold Deal Tagging sits on top of your CRM and comms and tags every open deal WARM, COOLING, COLD, or FROZEN based purely on prospect-side silence. Only a real inbound signal, an email reply, a meeting accept, an inbound message, resets the clock; rep follow-ups and stage nudges do not. For every cooling-or-worse deal it also surfaces exactly which engagement actions are missing, so reps know what to do next and forecasts stop counting dead weight.',
    why:
      'Sales pipelines routinely overstate health. A deal can sit at a stage for weeks with no inbound signal and still read as active revenue, which quietly inflates the forecast and burns rep time on dead deals. The trap is that rep activity, follow-ups, notes, stage edits, makes a deal look busy even when the prospect has ghosted. This plugin measures the only thing that matters, prospect-side response, and makes staleness impossible to hide.',
    use_case: [
      {
        step: '01',
        title: 'Connect CRM + comms',
        body: 'Pull active deals from Salesforce, HubSpot, or Pipedrive along with the full email, calendar, and messaging timeline for each. Thresholds are configurable per org and per deal segment (enterprise / SMB / transactional).',
      },
      {
        step: '02',
        title: 'Measure prospect silence only',
        body: 'days_inactive = today minus the last prospect-side signal (email reply, meeting accept, or inbound message). Rep follow-ups, CRM notes, and manual stage changes are ignored, they never reset the clock.',
      },
      {
        step: '03',
        title: 'Tag into inactivity tiers',
        body: 'Every deal is tagged WARM (<30d), COOLING (30-59d), COLD (60-89d), or FROZEN (90d+), with enterprise-friendly defaults and per-segment overrides. Tags render as colour-coded badges on every deal row.',
      },
      {
        step: '04',
        title: 'Surface missing actions + alert',
        body: 'For every cooling-or-worse deal, a checklist flags what is outstanding, no meeting proposed, no follow-up in N days, no response to the last outbound, no stage movement, and reps get an in-app alert the moment a deal turns COLD or FROZEN.',
      },
    ],
    highlights: [
      'Four inactivity tiers, WARM / COOLING / COLD / FROZEN, as colour-coded badges on every deal row.',
      'Only prospect-side signals reset the clock; rep follow-ups, notes, and stage edits are correctly ignored.',
      'Per-org and per-segment thresholds (enterprise vs SMB vs transactional), configurable without a deploy.',
      'Missing-action checklist per stalled deal: no meeting, no recent follow-up, no reply, no stage movement.',
      'Cold Pipeline Summary: deal count and value per tier, plus total value at risk (COLD + FROZEN).',
      'Week-over-week tier movement, so you see what escalated in the last 7 days.',
      'In-app alerts on COLD / FROZEN; CRM write-back and a daily rep digest on the roadmap.',
    ],
    inputs: [
      'Active deals from CRM (Salesforce, HubSpot, Pipedrive)',
      'Email, calendar, and messaging timeline per deal',
      'Deal owner, segment (enterprise / smb / transactional)',
      'Configurable inactivity thresholds per org & segment',
    ],
    outputs: [
      'cold_tag: WARM | COOLING | COLD | FROZEN per deal',
      'missing_actions: outstanding engagement actions per deal',
      'Cold Pipeline Summary (count + value per tier, value at risk)',
      'In-app alerts on tier escalation',
      'CRM write-back of cold_tag (roadmap)',
    ],
    works_with: ['Salesforce', 'HubSpot', 'Pipedrive', 'Gmail', 'Google Calendar', 'WhatsApp', 'Slack'],
    scopes: ['deal_read', 'timeline_read', 'deal_tag_write', 'alert_send'],
    performance: [
      { label: 'Tag computation', value: 'daily batch, real-time on the roadmap' },
      { label: 'Signals tracked', value: 'email replies, meeting accepts, inbound messages' },
      { label: 'Segments', value: 'enterprise / SMB / transactional thresholds' },
      { label: 'Typical value at risk surfaced', value: '15-25% of "active" pipeline' },
    ],
    screenshots: [
      {
        src: '/assets/plugins/pipeline-health-dashboard.webp',
        title: 'Pipeline Health view',
        caption:
          'Every deal tagged by prospect silence, with a summary widget showing count and value per tier and the total value at risk (COLD + FROZEN). Sorted by days inactive, filterable by tier.',
      },
      {
        src: '/assets/plugins/pipeline-health-deal.webp',
        title: 'Cold deal, with the receipts',
        caption:
          'Expand any deal to see the last prospect activity, days inactive, and a missing-action checklist, plus a timeline that shows exactly which events reset the clock (only the prospect reply did).',
      },
    ],
    preview: null,
  },

  {
    slug: 'incrementality-engine',
    name: 'Incrementality Engine',
    version: '0.8.0',
    tagline:
      'Separate genuinely new demand from orders you would have won anyway, and get a true cost per incremental order.',
    category: 'Growth · Causal Analytics',
    pricing: 'Early access',
    status: 'Early access',
    accent: '#6366f1',
    icon: 'trending-up',
    verified: true,
    author: 'Forsyt Labs',
    author_role: 'Built in-house · maintained by Forsyt',
    requested_by: {
      designation: 'VP of Growth',
      company: 'a large on-demand food-delivery & quick-commerce platform',
      region: 'India',
    },
    requested_context:
      'They were running a dozen growth bets in parallel, express delivery, instant grocery, dine-out, new-city launches, cashback pushes, and every one of them looked like a winner in its own dashboard. The problem was obvious to her and invisible to the numbers: a huge share of the orders each initiative claimed would have happened anyway through another channel. Cost per order looked artificially cheap, and money was being poured into initiatives that were mostly cannibalising each other. She wanted one honest number per initiative: net-new orders, cannibalization stripped out, and what an incremental order actually costs.',
    short:
      'Incrementality Engine measures the true, net-new impact of each growth initiative instead of the inflated number on its own dashboard. It runs three complementary causal methods continuously, encouragement design, a matched customer-cohort panel, and capacity-neutral switchbacks, and cross-validates them. When they converge, you get a trustworthy read: net incremental orders per day per initiative, cannibalization removed, and a true cost per incremental order. Everything upstream of a thin data connector is platform-agnostic.',
    why:
      'When several growth initiatives run at once, each one takes credit for orders that would have happened anyway through another channel. Every dashboard looks like a success, cost per order looks artificially low, and investment decisions get made on inflated numbers. Incrementality Engine strips out substitution and cannibalization so you fund what actually grows the business, not what merely reshuffles existing demand. No single method is trusted alone; the three are run together precisely because each corrects the others\u2019 blind spot.',
    use_case: [
      {
        step: '01',
        title: 'Encouragement design',
        body: 'Randomly vary how prominently an initiative is surfaced (sort order, banner, nudge) to a slice of eligible users. The feature stays live for everyone, only discovery is randomized, so there is no supply shock or operational disruption.',
      },
      {
        step: '02',
        title: 'Matched customer-cohort panel',
        body: 'Build a rolling behavioural history per customer and match adopters to statistically similar non-adopters on prior frequency, basket size, geo, and cuisine mix (propensity / double ML). This is also where cannibalization is measured directly.',
      },
      {
        step: '03',
        title: 'Capacity-neutral switchbacks',
        body: 'Short randomized on/off discovery windows within a single market, refreshed on a rolling schedule, give a continuous daily read that never goes stale.',
      },
      {
        step: '04',
        title: 'Triangulate & report',
        body: 'Cross-validate the three reads. When they converge within a tight tolerance band, the number is trusted and published, net incremental orders per day and true cost per incremental order, to a stakeholder dashboard and an API.',
      },
    ],
    highlights: [
      'Net incremental orders per day, per initiative, with cannibalization removed.',
      'True cost per incremental order, next to the (usually far lower) reported cost per order.',
      'Three causal methods run continuously; convergence within a tolerance band signals trust.',
      'Cannibalization measured directly from a matched cohort panel, broken out by cohort and city tier.',
      'Encouragement + switchback designs randomize discovery only, no supply shock, no ops disruption.',
      'Decoupled architecture: a thin connector maps your schema to a generic one, so it ports to any platform.',
      'Stakeholder dashboard plus an API so other tools consume the same honest numbers.',
    ],
    inputs: [
      'Order-level data with channel tagging and customer id',
      'Cost data per initiative (delivery, discounts, marketing)',
      'Customer-level order history across channels',
      'Existing experimentation / A-B infrastructure to build on',
      'Markets open to discovery-level randomization',
    ],
    outputs: [
      'Net incremental orders / day / initiative',
      'Cannibalization removed, by cohort and city tier',
      'True cost per incremental order',
      'Convergence / confidence read across the three methods',
      'Stakeholder dashboard + API',
    ],
    works_with: ['Snowflake', 'BigQuery', 'Redshift', 'Segment', 'Existing A/B infra', 'REST API'],
    scopes: ['order_data_read', 'cost_data_read', 'experiment_orchestrate', 'metrics_write'],
    performance: [
      { label: 'Methods', value: '3 causal designs cross-validated' },
      { label: 'Trust signal', value: 'convergence within \u00b11.5%' },
      { label: 'Read cadence', value: 'daily, always-on (switchback layer)' },
      { label: 'Portability', value: 'platform-agnostic behind a thin connector' },
    ],
    screenshots: [
      {
        src: '/assets/plugins/incrementality-dashboard.webp',
        title: 'Incrementality dashboard',
        caption:
          'Net incremental orders per day and true cost per incremental order up top, then a per-initiative breakdown of gross vs. substituted vs. net-new, with cannibalization and true CPO for each bet.',
      },
      {
        src: '/assets/plugins/incrementality-triangulation.webp',
        title: 'Triangulated read',
        caption:
          'The three causal methods side by side with their lift and confidence intervals. When they converge within the tolerance band the read is trusted; cannibalization is broken out by city tier below.',
      },
    ],
    preview: null,
  },

  {
    slug: 'local-demand-radar',
    name: 'Local Demand Radar',
    version: '1.1.0',
    tagline:
      'Every local event around any Indian pincode for the next year, mapped to the exact FMCG categories to push.',
    category: 'Field Sales · Demand Intelligence',
    pricing: 'Early access',
    status: 'Early access',
    accent: '#b45309',
    icon: 'map-pin',
    verified: true,
    author: 'Forsyt Labs',
    author_role: 'Built in-house · maintained by Forsyt',
    requested_by: {
      designation: 'National Sales Head',
      company: 'a mid-market FMCG company (packaged foods & personal care)',
      region: 'pan-India distribution, 400+ distributors',
    },
    requested_context:
      'His distributors knew their towns cold, but the knowledge lived in people\u2019s heads and left when they did. He kept seeing the same pattern: agarbatti and match sales quietly triple around a temple festival, stationery and biscuit demand spike near an exam centre in exam season, and nobody upstream planned for it. He asked for one thing, give a distributor a pincode and tell them what is about to happen there and what to stock. That is Local Demand Radar.',
    short:
      'Local Demand Radar is a demand-intelligence layer for field FMCG sales. Enter any Indian pincode and it returns every religious, educational, cultural, and civic event happening in and around it over the next 365 days, then recommends the product categories that reliably move around those events, with built-in compliance guardrails. Available as a dashboard for planners and as an API for your ordering and DMS stack.',
    why:
      'FMCG demand in India is deeply local and event-driven. A temple town runs on a festival calendar; a district with three coaching hubs runs on an exam calendar; a wedding-season belt moves entirely different SKUs in November than in July. National plans miss all of it. Local Demand Radar turns that local, tacit knowledge into a queryable map so a planner in the head office and a distributor on the ground are looking at the same demand picture, by pincode, week by week.',
    use_case: [
      {
        step: '01',
        title: 'Search a pincode',
        body: 'Type a pincode, or pick from suggested high-signal ones. Local Demand Radar resolves the location and pulls the full event landscape around it, temples and their festival calendars, exam centres, colleges, fairs, and civic gatherings within a configurable radius.',
      },
      {
        step: '02',
        title: 'See the demand landscape',
        body: 'A dashboard summarises total events over the next 365 days, the top recommended product categories ranked by expected pull, and a sensitivity note. Cards break out events by type, religious, educational, cultural, with dates, confidence (confirmed vs projected), and the categories each event tends to move.',
      },
      {
        step: '03',
        title: 'Map view for territory planning',
        body: 'A geographic view plots every event across a region so a sales manager can see clusters, plan routes, and spot under-served pockets of demand at a glance.',
      },
      {
        step: '04',
        title: 'Push to the field, with guardrails',
        body: 'Recommendations flow to distributors and to your ordering system via API. Compliance rules are baked in, for example, alcohol- and tobacco-adjacent promotions are automatically suppressed around fasting days and religious observances.',
      },
    ],
    highlights: [
      'Every event around any Indian pincode for the next 365 days, religious, educational, cultural, and civic.',
      'Category recommendations ranked by expected pull for each event and each pincode.',
      'Confirmed vs projected dating, so planners know what to commit stock against.',
      'Interactive territory map for route and cluster planning across a region.',
      'Compliance guardrails: auto-suppress sensitive promotions around fasting days and observances.',
      'Dashboard for planners plus a clean API for DMS, ordering, and route apps.',
    ],
    inputs: [
      'Pincode (single or bulk)',
      'Product category catalogue (optional, to tailor recommendations)',
      'Distributor territory definitions (optional)',
    ],
    outputs: [
      'Event landscape per pincode (type, date, confidence, radius)',
      'Ranked category recommendations per pincode and per event',
      'Sensitivity / compliance advisories',
      'Territory map layer (GeoJSON)',
      'API responses for ordering / DMS integration',
    ],
    works_with: ['REST API', 'DMS / ordering systems', 'CSV', 'Route-to-market apps', 'Webhooks'],
    scopes: ['location_lookup', 'events_calendar_read', 'catalogue_read'],
    sources: [
      'Temple & festival calendars (Panchang-aligned observances)',
      'Public examination schedules (national & state boards, competitive exams)',
      'College & institutional academic calendars',
      'Civic & cultural event registries',
      'Historical FMCG category-movement patterns',
    ],
    performance: [
      { label: 'Coverage', value: 'All Indian pincodes, 365-day horizon' },
      { label: 'Events indexed', value: '5,000+ per dense pincode' },
      { label: 'Dashboard response', value: 'sub-second for a resolved pincode' },
      { label: 'Category-pull accuracy', value: 'validated against 2 seasons of sell-through' },
    ],
    screenshots: [
      {
        src: '/assets/plugins/meridian-dashboard.webp',
        title: 'Pincode demand dashboard',
        caption:
          'Enter a pincode (here 625001, Madurai) and Local Demand Radar returns total events, the top recommended categories, a compliance advisory, nearby educational events, and the temples driving festival demand.',
      },
      {
        src: '/assets/plugins/meridian-map.webp',
        title: 'Territory event map',
        caption:
          'Every event across a region plotted on one map, so a manager can see demand clusters and plan routes at a glance.',
      },
    ],
    preview: null,
  },

  {
    slug: 'pipeline-conversion-engine',
    name: 'Pipeline Conversion Engine',
    version: '1.0.3',
    tagline:
      'Cut the MQL-to-SQL drop by targeting accounts that look like your winners, then personalising every message.',
    category: 'Marketing · Pipeline Conversion',
    pricing: 'Private beta',
    status: 'Private beta',
    accent: '#0d9488',
    icon: 'filter',
    verified: true,
    author: 'Forsyt Labs',
    author_role: 'Built in-house · maintained by Forsyt',
    requested_by: {
      designation: 'Head of Growth',
      company: 'a Series C B2B SaaS scale-up (infrastructure software)',
      region: 'North America & EMEA',
    },
    requested_context:
      'Marketing was hitting its MQL number every quarter and sales was quietly furious, because barely a third of those MQLs ever became SQLs. The Head of Growth was convinced the leak was not the volume, it was the mix and the message: too many off-ICP accounts, and outreach that read like a mail merge. She wanted a tool that started from the customers they already won, found more that looked like them, scored them honestly, and then wrote to the actual humans on the buying committee like it knew them. That is Pipeline Conversion Engine.',
    short:
      'Pipeline Conversion Engine attacks the MQL-to-SQL drop-off in one flow. It learns your ICP from the customers you already win, builds a lookalike account list, scores and grades every account for fit, and then, for the specific people you choose to reach, generates personality-aware, personalised messaging. Fewer wrong accounts in the funnel, sharper outreach on the right ones.',
    why:
      'The MQL-to-SQL stage is where most B2B pipelines silently bleed. Two causes dominate: reps work accounts that were never a real fit, and they send outreach generic enough to ignore. Pipeline Conversion Engine addresses both in sequence, right list, then right message, instead of treating them as separate tools bolted together. Because it starts from your closed-won data rather than a generic template, "good fit" means good fit for you, not for the industry average.',
    use_case: [
      {
        step: '01',
        title: 'Learn from your winners',
        body: 'Feed Pipeline Conversion Engine up to five domains of accounts you already win. It reverse-engineers the shared shape, firmographics, tech stack, size, region, and uses that as the seed for what a great-fit account looks like.',
      },
      {
        step: '02',
        title: 'Build the right list',
        body: 'Set floors and restrictions, minimum revenue and headcount, target locations, industries, and how deep to search. Pipeline Conversion Engine generates a scored list of lookalike accounts. Every row carries a fit score, a grade, and the tech-stack tags that matter to your play.',
      },
      {
        step: '03',
        title: 'Score and prioritise',
        body: 'Accounts are ranked and graded (A to F) with a fit score and priority band, so reps work the P0 A-grades first. Export to CSV or save and score into the workspace to keep them live.',
      },
      {
        step: '04',
        title: 'Personalise to the person',
        body: 'For the specific people you select to message, Pipeline Conversion Engine builds a personality read (dominance, influence, steadiness, calculativeness) and turns it into concrete guidance, what to say, what to avoid, and the decision drivers, then drafts a personality-aware email in one click.',
      },
    ],
    highlights: [
      'Lookalike list built from your own closed-won accounts, not a generic ICP template.',
      'Fit score plus A-to-F grade and priority band on every account.',
      'Tech-stack and intent tags surfaced per account for play targeting.',
      'Floors and restrictions: revenue, headcount, geography, industry, search depth.',
      'Per-person personality read (DISC-style) with say / avoid / decision-driver guidance.',
      'One-click personality-aware email drafting for the people you choose to contact.',
      'Save & score into the workspace or export the whole list to CSV.',
    ],
    inputs: [
      'Up to 5 seed domains (accounts you already win)',
      'Floors & restrictions (revenue, headcount, geography, industry)',
      'Selected contacts to personalise for',
    ],
    outputs: [
      'Scored, graded lookalike account list',
      'Fit score, grade, and priority band per account',
      'Per-person personality profile and messaging guidance',
      'Draft personality-aware emails',
      'CSV export or save & score into the workspace',
    ],
    works_with: ['HubSpot', 'Salesforce', 'Outreach', 'Salesloft', 'CSV', 'Webhooks'],
    scopes: ['account_list_write', 'firmographic_lookup', 'contact_read', 'icp_definition_read'],
    sources: [
      'Your closed-won accounts (ICP seed)',
      'Firmographic & technographic providers (lookalike expansion)',
      'Public professional profiles (personality inference)',
      'Company website & tech-stack detection',
    ],
    performance: [
      { label: 'List generation', value: '100 scored accounts in under a minute' },
      { label: 'Fit-score correlation', value: '0.71 vs. actual SQL conversion (pilot)' },
      { label: 'MQL\u2192SQL lift', value: '+22% on treated segments (pilot)' },
      { label: 'Draft time', value: 'personality-aware email in ~4s' },
    ],
    screenshots: [
      {
        src: '/assets/plugins/cascade-build.webp',
        title: 'Build the right list',
        caption:
          'Seed with domains you already win, set floors and restrictions, and generate a scored, graded list of lookalike accounts.',
      },
      {
        src: '/assets/plugins/cascade-score.webp',
        title: 'Score & prioritise',
        caption:
          'Every account ranked with a fit score, priority band, tech-stack tags, and an A-to-F grade, so reps work the best fits first.',
      },
      {
        src: '/assets/plugins/cascade-personalise.webp',
        title: 'Personalise to the person',
        caption:
          'A per-person personality read turns into say / avoid / decision-driver guidance and a one-click personality-aware email.',
      },
    ],
    preview: null,
  },
];

export const getPlugin = (slug) => PLUGINS.find((p) => p.slug === slug);

// Community stat for the Plugin Store header. Illustrative, but realistic.
export const DEVELOPERS_BUILDING = 203;
